// src/components/hooks/useEmpresa.ts
import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

// ============================================================
// TIPOS
// ============================================================

export interface TrabajadorEmpresa {
  id_usuario: string;
  nombre: string;
  email: string | null;
  telefono: string | null;
  foto: string | null;
}

export interface FaseServicio {
  id_fase: string;
  nombre_fase: string;
  duracion_minutos: number;
  requiere_atencion: boolean;
  orden: number;
}

export interface ServicioEmpresa {
  id_servicio: string;
  servicio: string;
  descripcion: string | null;
  precio: number;
  precio_aplica?: string | null;
  cliente_min?: number | null;
  cliente_max?: number | null;
  duracion_minutos: number;
  icono?: string | null;
  activo: boolean;
  fases_servicio?: FaseServicio[] | null;
  reservable_online?: boolean;
  reserva_por_horas?: boolean;
  horas_min?: number | null;
  horas_max?: number | null;
}

export interface CategoriaServicioEmpresa {
  id_categoria_servicio: string | null;
  nombre: string;
  imagen: string | null;
  icono: string | null;
  orden: number;
  servicios: ServicioEmpresa[];
}

export interface BonoEmpresa {
  id_bono: string;
  nombre_bono: string;
  descripcion: string | null;
  precio_total: number;
  cantidad_servicios: number;
  duracion_dias: number;
  activo: boolean;
}

export interface RedSocial {
  id_tipo_red: string;
  nombre: string;
  url: string;
  icono?: string | null;
}

export interface SubcategoriaEmpresa {
  id_subcategoria: string;
  subcategoria: string;
}

export interface FotoEmpresa {
  id_foto: string;
  url_foto: string;
  titulo?: string | null;
  descripcion?: string | null;
  destacado?: boolean;
  fecha_creacion?: string;
}

export interface HorarioEmpresa {
  id_horario: string;
  dia_semana: string;
  hora_inicio: string;
  hora_fin: string;
  activo: boolean;
}

export interface ValoracionEmpresa {
  id_valoracion: string;
  puntuacion: number;
  comentario: string;
  fecha_valoracion: string;
  nombre_cliente: string;
}

export interface ProductoEmpresa {
  id_producto: string;
  nombre: string;
  descripcion: string | null;
  precio: number | null;
  imagen_principal: string | null;
  orden: number;
  activo: boolean;
}

export interface EmpresaDetalle {
  id_empresa: string;
  categoria_slug?: string;
  ciudad_slug?: string;
  empresa_slug?: string;
  empresa: string;
  email: string | null;
  telefono: string | null;
  descripcion: string | null;
  descripcion_corta?: string | null;
  logo: string | null;
  privada: boolean;
  pais: string | null;
  ciudad: string | null;
  localidad: string | null;
  direccion: string | null;
  latitud?: number | null;
  longitud?: number | null;
  id_categoria: string;
  categoria?: string | null;
  color_primario: string | null;
  color_secundario: string | null;
  reservas_online_citas: boolean;
  reservas_online_eventos: boolean;
  permite_reservas_solo_clientes: boolean;
  dias_visibles_reserva: number | null;
  notifica_cliente_email: boolean;
  notifica_trabajador_email: boolean;
  notifica_gestor_email: boolean;
  notifica_cliente_sms: boolean;
  notifica_trabajador_sms: boolean;
  notifica_gestor_sms: boolean;
  notifica_cliente_whatsapp: boolean;
  notifica_trabajador_whatsapp: boolean;
  notifica_gestor_whatsapp: boolean;
  recordatorio_email: number | null;
  recordatorio_sms: number | null;
  recordatorio_whatsapp: number | null;
  recordatorio_email_activo: boolean;
  recordatorio_sms_activo: boolean;
  recordatorio_whatsapp_activo: boolean;
  idioma: string | null;
  zona_horaria: string | null;
  configuracion_basica?: boolean;
  activo?: boolean;
  es_fundador_diamante?: boolean | null;
  es_fundador_platino?: boolean | null; 
  trabajadores: TrabajadorEmpresa[];
  servicios: ServicioEmpresa[];
  categorias_servicios: CategoriaServicioEmpresa[];
  bonos: BonoEmpresa[];
  redes_sociales: RedSocial[];
  subcategorias: SubcategoriaEmpresa[];
  fotos: FotoEmpresa[];
  horario_empresa: HorarioEmpresa[];
  valoraciones_empresa?: ValoracionEmpresa[];
  productos: ProductoEmpresa[];
  valoracion_media_empresa?: number | null;
  total_valoraciones_empresa?: number;
  id_plan?: string | null;
  nombre_plan?: string | null;
  tiene_plan_activo?: boolean;
}

/* Normaliza los arrays anidados que pueden venir como null desde la vista */
const normalizeEmpresa = (data: any): EmpresaDetalle => ({
  ...data,
  trabajadores: data.trabajadores ?? [],
  servicios: data.servicios ?? [],
  categorias_servicios: data.categorias_servicios ?? [],
  bonos: data.bonos ?? [],
  redes_sociales: data.redes_sociales ?? [],
  subcategorias: data.subcategorias ?? [],
  fotos: data.fotos ?? [],
  horario_empresa: data.horario_empresa ?? [],
  valoraciones_empresa: data.valoraciones_empresa ?? [],
  productos: data.productos ?? [],
});

// ============================================================
// HOOK PRINCIPAL (POR ID_EMPRESA)
// ============================================================

export const useEmpresa = (
  id_empresa?: string,
  vista:
    | 'vista_empresa_detalle'
    | 'vista_configuracion_empresa' = 'vista_empresa_detalle'
) => {
  const [empresa, setEmpresa] = useState<EmpresaDetalle | null>(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);
  const [errorEmpresa, setErrorEmpresa] = useState<string | null>(null);

  const updateEmpresaLocal = (patch: Partial<EmpresaDetalle>) => {
    setEmpresa((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const saveEmpresa = async (data: Partial<EmpresaDetalle>) => {
    if (!empresa?.id_empresa) return { error: 'Empresa no cargada' };

    const usuario = localStorage.getItem('id_usuario');

    const { error } = await supabase
      .from('empresas')
      .update({
        ...data,
        usuario_edicion: usuario || null,
        fecha_edicion: new Date(),
      })
      .eq('id_empresa', empresa.id_empresa);

    if (error) {
      Sentry.captureException(error, {
        extra: { id_empresa: empresa.id_empresa },
      });
      toast.error('Error al guardar los cambios.');
      return { error };
    }

    updateEmpresaLocal(data);
    toast.success('Cambios guardados.');
    return { error: null };
  };

  const fetchEmpresa = useCallback(async () => {
    if (!id_empresa) return;

    setLoadingEmpresa(true);
    setErrorEmpresa(null);

    const { data, error } = await supabase
      .from(vista)
      .select('*')
      .eq('id_empresa', id_empresa)
      .single();

    if (error) {
      Sentry.captureException(error, { extra: { id_empresa, vista } });
      setErrorEmpresa('Error al cargar empresa');
      toast.error('Error al cargar empresa');
      setLoadingEmpresa(false);
      return;
    }

    setEmpresa(normalizeEmpresa(data));
    if (data.email) localStorage.setItem('email_empresa', data.email);
    setLoadingEmpresa(false);
  }, [id_empresa, vista]);

  useEffect(() => {
    fetchEmpresa();
  }, [fetchEmpresa]);

  return {
    empresa,
    loadingEmpresa,
    errorEmpresa,
    refetchEmpresa: fetchEmpresa,
    updateEmpresaLocal,
    saveEmpresa,
  };
};

// ============================================================
// HOOK SEO (POR SLUGS)
// ============================================================

export const useEmpresaBySlug = (
  categoria_slug?: string,
  ciudad_slug?: string,
  empresa_slug?: string
) => {
  const [empresa, setEmpresa] = useState<EmpresaDetalle | null>(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(false);
  const [errorEmpresa, setErrorEmpresa] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<{
    logo: string | null;
    empresa: string;
  } | null>(null);

  const fetchEmpresa = useCallback(async () => {
    if (!categoria_slug || !ciudad_slug || !empresa_slug) return;

    setLoadingEmpresa(true);
    setErrorEmpresa(null);
    setLogoPreview(null);

    // Consulta ligera en paralelo para mostrar el logo cuanto antes.
    supabase
      .from('vista_empresa_detalle')
      .select('logo, empresa')
      .eq('categoria_slug', categoria_slug)
      .eq('ciudad_slug', ciudad_slug)
      .eq('empresa_slug', empresa_slug)
      .single()
      .then(({ data }) => {
        if (data) setLogoPreview({ logo: data.logo, empresa: data.empresa });
      });

    const { data, error } = await supabase
      .from('vista_empresa_detalle')
      .select('*')
      .eq('categoria_slug', categoria_slug)
      .eq('ciudad_slug', ciudad_slug)
      .eq('empresa_slug', empresa_slug)
      .single();

    if (error) {
      Sentry.captureException(error, {
        extra: { categoria_slug, ciudad_slug, empresa_slug },
      });
      setErrorEmpresa('Empresa no encontrada');
      toast.error('Empresa no encontrada');
      setLoadingEmpresa(false);
      return;
    }

    setEmpresa(normalizeEmpresa(data));
    setLoadingEmpresa(false);
  }, [categoria_slug, ciudad_slug, empresa_slug]);

  useEffect(() => {
    fetchEmpresa();
  }, [fetchEmpresa]);

  return {
    empresa,
    loadingEmpresa,
    errorEmpresa,
    logoPreview,
  };
};

